import type { InstructionStep, Recipe } from '../types';

export interface GroupedInstructionSection {
    header: string;
    steps: InstructionStep[];
}

export function groupRecipeInstructions(
    recipe: Recipe,
    defaultSectionLabel: string
): GroupedInstructionSection[] {
    const instructions = recipe.instructions;
    let rawSteps: InstructionStep[] = [];

    if (typeof instructions === 'string') {
        rawSteps = instructions
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line, index) => ({
                id: `s-${index}`,
                type: 'step' as const,
                content: line.replace(/^\d+[\.\)\-\s]+/, '').trim(),
            }));
    } else {
        rawSteps = instructions;
    }

    const sections: GroupedInstructionSection[] = [];
    let current: GroupedInstructionSection | null = null;

    rawSteps.forEach((step) => {
        if (step.type === 'header') {
            current = { header: step.content, steps: [] };
            sections.push(current);
            return;
        }

        if (!current) {
            current = { header: defaultSectionLabel, steps: [] };
            sections.push(current);
        }
        current.steps.push(step);
    });

    return sections;
}

export function getRecipeStepKeys(
    recipeId: string,
    sections: GroupedInstructionSection[]
): string[] {
    return sections.flatMap((section, sectionIndex) =>
        section.steps.map((_, stepIndex) => `${recipeId}:${sectionIndex}-${stepIndex}`)
    );
}

export function isRecipeStepsComplete(
    recipeId: string,
    sections: GroupedInstructionSection[],
    completedSteps: ReadonlySet<string>
): boolean {
    const keys = getRecipeStepKeys(recipeId, sections);
    if (keys.length === 0) return false;
    return keys.every((key) => completedSteps.has(key));
}

export function areAllMealRecipesStepsComplete(
    recipes: { recipe: Recipe }[],
    completedSteps: ReadonlySet<string>,
    defaultSectionLabel: string,
    completedRecipes?: ReadonlySet<string>
): boolean {
    if (recipes.length === 0) return false;

    return recipes.every((mealRecipe) => {
        if (completedRecipes?.has(mealRecipe.recipe.id)) return true;
        const sections = groupRecipeInstructions(mealRecipe.recipe, defaultSectionLabel);
        return isRecipeStepsComplete(mealRecipe.recipe.id, sections, completedSteps);
    });
}

function normalizeStepContent(content: string): string {
    return content
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, '')
        .replace(/\s+/g, ' ')
        .trim();
}

export function resolveSessionStepKeyForTimelineStep(
    recipeId: string,
    recipe: Recipe,
    analyzedStep: { stepIndex: number; content: string; sectionHeader: string },
    defaultSectionLabel: string
): string | null {
    const sections = groupRecipeInstructions(recipe, defaultSectionLabel);
    const flatSteps: { sectionIndex: number; stepIndex: number; content: string; sectionHeader: string }[] = [];

    sections.forEach((section, sectionIndex) => {
        section.steps.forEach((step, stepIndex) => {
            flatSteps.push({
                sectionIndex,
                stepIndex,
                content: step.content,
                sectionHeader: section.header,
            });
        });
    });

    if (flatSteps.length === 0) return null;

    const normalizedTarget = normalizeStepContent(analyzedStep.content);
    const normalizedSection = normalizeStepContent(analyzedStep.sectionHeader);

    const sectionSteps = flatSteps.filter((step) => {
        const normalizedStepSection = normalizeStepContent(step.sectionHeader);
        return normalizedStepSection === normalizedSection
            || normalizedStepSection.includes(normalizedSection)
            || normalizedSection.includes(normalizedStepSection);
    });
    const searchPool = sectionSteps.length > 0 ? sectionSteps : flatSteps;

    const exactMatch = searchPool.find((step) => normalizeStepContent(step.content) === normalizedTarget);
    if (exactMatch) {
        return `${recipeId}:${exactMatch.sectionIndex}-${exactMatch.stepIndex}`;
    }

    const partialMatch = searchPool.find((step) => {
        const normalized = normalizeStepContent(step.content);
        return normalized.includes(normalizedTarget) || normalizedTarget.includes(normalized);
    });
    if (partialMatch) {
        return `${recipeId}:${partialMatch.sectionIndex}-${partialMatch.stepIndex}`;
    }

    if (sectionSteps.length > 0) {
        const stepInSection = sectionSteps[analyzedStep.stepIndex];
        if (stepInSection) {
            return `${recipeId}:${stepInSection.sectionIndex}-${stepInSection.stepIndex}`;
        }
    }

    const flat = flatSteps[analyzedStep.stepIndex];
    if (flat) {
        return `${recipeId}:${flat.sectionIndex}-${flat.stepIndex}`;
    }

    return null;
}

export function getTimelineSegmentSessionKey(
    segment: { recipeId: string; stepIndex: number; content: string; sectionHeader: string },
    analyzedRecipe: { steps: { stepIndex: number; content: string; sectionHeader: string }[] } | undefined,
    recipe: Recipe | undefined,
    defaultSectionLabel: string
): string | null {
    if (!analyzedRecipe || !recipe) return null;
    const analyzedStep = analyzedRecipe.steps.find((step) => step.stepIndex === segment.stepIndex);
    if (!analyzedStep) return null;
    return resolveSessionStepKeyForTimelineStep(recipe.id, recipe, analyzedStep, defaultSectionLabel);
}

export function isTimelineSegmentComplete(
    recipeId: string,
    sessionStepKey: string | null,
    completedSteps: ReadonlySet<string>,
    completedRecipes: ReadonlySet<string>
): boolean {
    if (completedRecipes.has(recipeId)) return true;
    if (!sessionStepKey) return false;
    return completedSteps.has(sessionStepKey);
}

export interface PlannedRecipeSessionProgress {
    completedRecipes: ReadonlySet<string>;
    completedSteps: ReadonlySet<string>;
    struckIngredients: ReadonlySet<string>;
    preppedIngredients: ReadonlySet<string>;
}

export function isPlannedRecipeCookingDone(
    recipeId: string,
    recipe: import('../types').Recipe,
    session: Pick<PlannedRecipeSessionProgress, 'completedRecipes' | 'completedSteps'> | undefined,
    prepTabLabel: string
): boolean {
    if (!session) return false;
    if (session.completedRecipes.has(recipeId)) return true;
    const sections = groupRecipeInstructions(recipe, prepTabLabel);
    return isRecipeStepsComplete(recipeId, sections, session.completedSteps);
}

export function getRecipePrepIngredientKey(recipeId: string, name: string, unit: string): string {
    return `${recipeId}:${name}-${unit}`;
}

export function getRecipeStruckIngredientKey(
    recipeId: string,
    section: string,
    name: string,
    unit: string
): string {
    return `${recipeId}|${section}|${name}|${unit}`;
}

export function isRecipeStruckIngredientKey(key: string, recipeId: string): boolean {
    if (key.startsWith(`${recipeId}|`)) return true;
    return key.startsWith(`${recipeId}-`);
}

export function isPlannedRecipeCookingInProgress(
    recipeId: string,
    recipe: import('../types').Recipe,
    session: PlannedRecipeSessionProgress | undefined,
    prepTabLabel: string
): boolean {
    if (!session) return false;
    if (isPlannedRecipeCookingDone(recipeId, recipe, session, prepTabLabel)) return false;

    const stepPrefix = `${recipeId}:`;
    if ([...session.completedSteps].some((key) => key.startsWith(stepPrefix))) {
        return true;
    }

    if ([...session.struckIngredients].some((key) => isRecipeStruckIngredientKey(key, recipeId))) {
        return true;
    }

    return recipe.ingredients.some((ingredient) =>
        session.preppedIngredients.has(getRecipePrepIngredientKey(recipeId, ingredient.name, ingredient.unit))
    );
}
